/**
 * surface — the wall's touch/pointer decision, in one place (code-quality review F4/F5/F12).
 * `Wall.tsx` (which surface mounts) and `Column.tsx` (its own coarse-pointer guard against summoning
 * a keyboard nobody asked to open) each read `window.matchMedia('(pointer: coarse)')` independently
 * before this file existed — one guarded against a missing `matchMedia`, the other did not (F12), and
 * nothing stopped the two from drifting the way `Column.tsx`'s own width comment already had (F5).
 */

/** Guarded: `matchMedia` is a browser-only API, and this module's two callers are both client-only
 *  already (a layout effect, a `useState` initialiser that never runs during SSR) — the guard is
 *  belt-and-braces for any future caller that is not. */
export function coarsePointer(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches
}

const MOBILE_BREAKPOINT = 640
/** At or below this width a touch-first device gets the sheet whatever `innerWidth` says — a phone
 *  in landscape (844–932), a fold unfolded (~673–840), a phone in "request desktop site" (980), a
 *  tablet in portrait (up to 1024, the 12.9" iPad Pro's own portrait width — F4's second-order
 *  finding: the boundary is inclusive so that device lands on the Sheet like every other portrait
 *  tablet, not on the Column by one point of width). A tablet in landscape (beyond 1024) keeps the
 *  column. */
const TOUCH_SHEET_MAX_WIDTH = 1024

/**
 * Sheet or column. Width alone (the 640 CSS breakpoint) decided this until 2026-09-07, and a phone
 * would then land on the column whenever its `innerWidth` read above 640 (operator-observed on a
 * phone, "sometimes the desktop one"): landscape, a fold and desktop mode do that by construction;
 * iOS widening the layout viewport around the SSR rows — laid out at `SSR_CONTAINER_WIDTH` under an
 * `overflow-x: visible` body — is the unconfirmed candidate for the same phone in portrait. The
 * primary pointer is read off the input instead, which no viewport arithmetic can move:
 * `(pointer: coarse)` is a finger, and a finger gets the sheet.
 */
export function isSheetSurface(): boolean {
    if (window.innerWidth <= MOBILE_BREAKPOINT) return true
    return coarsePointer() && window.innerWidth <= TOUCH_SHEET_MAX_WIDTH
}
